import React from 'react';
import { array, object, oneOf, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import { ROLES } from '../../../util/orgRoles';
import { Avatar, Icon, Menu, MenuContent, MenuItem, MenuLabel, NamedLink } from '../..';
import { resolveSwitcherTarget } from './dashboardRouteEquivalence';

import css from './DashboardSwitcher.module.css';

const KNOWN_ROLES = Object.values(ROLES);

// The picture the creator uploaded on their creator profile, with the initials as the fallback.
// Only that link is passed on, so the Avatar never asks Sharetribe for a profile image variant.
const creatorAvatarUser = currentUser => {
  const profile = currentUser?.attributes?.profile || {};
  return {
    id: currentUser?.id || null,
    type: 'user',
    attributes: {
      profile: {
        displayName: profile.displayName,
        abbreviatedName: profile.abbreviatedName,
        publicData: { profileImage: profile.publicData?.profileImage || null },
      },
    },
  };
};

// An organization as an Avatar user: its logo link (or URL string) and its first letter.
const orgAvatarUser = org => {
  const link =
    org?.profileImage?.link || (typeof org?.profileImage === 'string' && org.profileImage);
  return {
    id: null,
    type: 'user',
    attributes: {
      profile: {
        displayName: org?.name,
        abbreviatedName: org?.name?.charAt(0)?.toUpperCase(),
        publicData: { avatarImage: link ? { link } : null },
      },
    },
  };
};

const SwitcherAvatar = ({ user, organization, size }) => (
  <Avatar
    user={user}
    size={size}
    organization={organization}
    disableProfileLink
    useCreatorAvatar={!organization}
  />
);

const OrgKind = ({ role }) =>
  KNOWN_ROLES.includes(role) ? (
    <FormattedMessage id="DashboardSwitcher.organizationRole" values={{ role }} />
  ) : (
    <FormattedMessage id="DashboardSwitcher.organization" />
  );

const DashboardRow = ({ linkProps, selected, avatar, name, kind }) => (
  <NamedLink {...linkProps} className={classNames(css.row, { [css.rowSelected]: selected })}>
    {avatar}
    <span className={css.rowText}>
      <span className={css.rowName}>{name}</span>
      <span className={css.rowKind}>{kind}</span>
    </span>
    {selected ? <Icon name="check" size={16} className={css.check} /> : null}
  </NamedLink>
);

/**
 * The dashboard switcher at the bottom of the dashboard sidebar (board DashSwitcher): who the
 * dashboard belongs to, and a popover to switch to the creator dashboard or another organization's,
 * create an organization, view the public profile, or go back to the audience side. With
 * `layout="bar"` it is the left of the phone top bar (PhoneDash): "Creator dashboard ▾", and the
 * popover opens below.
 *
 * Switching lands on the equivalent page on the other side (dashboardRouteEquivalence.js).
 */
const DashboardSwitcher = props => {
  const {
    rootClassName,
    className,
    currentUser,
    organizationProfiles,
    // The organization whose dashboard this is (from the URL); none is the creator dashboard
    currentOrgId,
    // The page we're on, to land on the equivalent page after switching
    currentPage,
    layout,
  } = props;
  const isBar = layout === 'bar';

  const profile = currentUser?.attributes?.profile || {};
  const userDisplayName = profile.displayName || '';
  const firstName = profile.firstName || userDisplayName;
  const userId = currentUser?.id?.uuid;

  const isCreatorContext = !currentOrgId;
  const organizations = organizationProfiles || [];
  const selectedOrganization = organizations.find(org => org.id === currentOrgId);
  const creatorUser = creatorAvatarUser(currentUser);

  const creatorTarget = resolveSwitcherTarget({ currentPage, targetContext: 'creator' });
  const profileLink = isCreatorContext
    ? { name: 'CreatorProfilePage', params: { id: userId } }
    : { name: 'OrganizationProfilePage', params: { id: currentOrgId } };
  const createOrgLink = currentOrgId
    ? { name: 'NewOrganizationPage', params: { orgId: currentOrgId } }
    : { name: 'CreatorNewOrganizationPage' };

  return (
    <Menu
      rootClassName={rootClassName || (isBar ? css.rootBar : css.root)}
      className={className}
      contentPosition="right"
      useArrow={false}
    >
      {isBar ? (
        <MenuLabel rootClassName={css.barTrigger} isOpenClassName={css.barTriggerOpen}>
          {isCreatorContext ? (
            <SwitcherAvatar user={creatorUser} size={30} />
          ) : (
            <SwitcherAvatar user={orgAvatarUser(selectedOrganization)} size={30} organization />
          )}
          <span className={css.rowText}>
            <span className={css.barName}>
              {isCreatorContext ? userDisplayName : selectedOrganization?.name}
            </span>
            <span className={css.barKind}>
              <FormattedMessage
                id={
                  isCreatorContext
                    ? 'TopbarDesktop.creatorDashboard'
                    : 'DashboardSwitcher.organization'
                }
              />
              <Icon name="chevronDown" size={14} />
            </span>
          </span>
        </MenuLabel>
      ) : (
        <MenuLabel rootClassName={css.trigger} isOpenClassName={css.triggerOpen}>
          {isCreatorContext ? (
            <SwitcherAvatar user={creatorUser} size={32} />
          ) : (
            <SwitcherAvatar user={orgAvatarUser(selectedOrganization)} size={32} organization />
          )}
          <span className={css.rowText}>
            <span className={css.triggerName}>
              {isCreatorContext ? userDisplayName : selectedOrganization?.name}
            </span>
            <span className={css.rowKind}>
              <FormattedMessage
                id={
                  isCreatorContext
                    ? 'DashboardSwitcher.creatorSwitch'
                    : 'DashboardSwitcher.organizationSwitch'
                }
              />
            </span>
          </span>
          <Icon name="chevronUpDown" size={14} className={css.chevrons} />
        </MenuLabel>
      )}
      <MenuContent
        rootClassName={isBar ? css.popoverBelow : css.popover}
        contentClassName={css.list}
      >
        <MenuItem key="caption" type="caption">
          <FormattedMessage id="DashboardSwitcher.switchDashboard" />
        </MenuItem>

        <MenuItem key="creator">
          <DashboardRow
            linkProps={{ name: creatorTarget.name }}
            selected={isCreatorContext}
            avatar={<SwitcherAvatar user={creatorUser} size={30} />}
            name={userDisplayName}
            kind={<FormattedMessage id="DashboardSwitcher.creator" />}
          />
        </MenuItem>

        {organizations.map(org => {
          const target = resolveSwitcherTarget({
            currentPage,
            targetContext: 'org',
            targetOrgId: org.id,
          });
          return (
            <MenuItem key={org.id}>
              <DashboardRow
                linkProps={{ name: target.name, params: target.params }}
                selected={currentOrgId === org.id}
                avatar={<SwitcherAvatar user={orgAvatarUser(org)} size={30} organization />}
                name={org.name}
                kind={<OrgKind role={org.role} />}
              />
            </MenuItem>
          );
        })}

        <MenuItem key="create-org">
          <NamedLink {...createOrgLink} className={classNames(css.row, css.createRow)}>
            <span className={css.createTile} aria-hidden="true">
              <Icon name="add" size={14} />
            </span>
            <FormattedMessage id="DashboardSwitcher.createOrganization" />
          </NamedLink>
        </MenuItem>

        <MenuItem key="separator" type="separator" />

        {/* Hidden while the signed-in user is still loading: the link needs their id */}
        <MenuItem key="profile" isHidden={isCreatorContext && !userId}>
          <NamedLink {...profileLink} className={css.row}>
            <Icon name="person" size={16} className={css.rowIcon} />
            <span className={css.rowText}>
              <FormattedMessage
                id={
                  isCreatorContext
                    ? 'DashboardSwitcher.viewCreatorProfile'
                    : 'DashboardSwitcher.viewOrganizationPage'
                }
              />
            </span>
          </NamedLink>
        </MenuItem>

        <MenuItem key="audience">
          <NamedLink name="LandingPage" className={css.row}>
            <Icon name="external" size={16} className={css.rowIcon} />
            <span className={css.rowText}>
              <FormattedMessage id="DashboardSwitcher.switchToAudience" />
              <span className={css.rowKind}>
                <FormattedMessage id="DashboardSwitcher.browseAs" values={{ name: firstName }} />
              </span>
            </span>
          </NamedLink>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

DashboardSwitcher.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  organizationProfiles: [],
  currentOrgId: null,
  currentPage: null,
  layout: 'sidebar',
};

DashboardSwitcher.propTypes = {
  rootClassName: string,
  className: string,
  currentUser: object,
  // { id, name, profileImage, role } for each organization the user owns or belongs to
  organizationProfiles: array,
  currentOrgId: string,
  currentPage: string,
  // 'sidebar' at the bottom of the sidebar, 'bar' in the phone top bar
  layout: oneOf(['sidebar', 'bar']),
};

export default DashboardSwitcher;
